import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import api from '../lib/api';

const timeAgo = (iso) => {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'baru saja';
  if (diff < 3600) return `${Math.floor(diff / 60)} mnt lalu`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} jam lalu`;
  return `${Math.floor(diff / 86400)} hari lalu`;
};

export function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const r = await api.get('/notifications');
      setItems(r.data.items || []);
      setUnread(r.data.unread || 0);
    } catch (e) { /* lonceng diam saja bila gagal */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const openItem = async (n) => {
    if (!n.read) {
      try { await api.patch(`/notifications/${n.id}/read`); } catch (e) { /* ignore */ }
    }
    setOpen(false);
    load();
    if (n.link) navigate(n.link);
  };

  const readAll = async () => {
    try { await api.patch('/notifications/read-all'); } catch (e) { /* ignore */ }
    load();
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) load(); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" className="relative" title="Notifikasi" data-testid="notification-bell">
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-unread-count">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <p className="text-sm font-semibold">Notifikasi <span className="text-xs text-slate-500 font-normal">(30 hari terakhir)</span></p>
          {unread > 0 && (
            <button className="text-xs text-blue-700 flex items-center gap-1 hover:underline" onClick={readAll} data-testid="notification-read-all">
              <CheckCheck className="h-3 w-3" /> Tandai semua dibaca
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && <p className="text-sm text-slate-500 text-center py-6" data-testid="notification-empty">Belum ada notifikasi</p>}
          {items.map((n) => (
            <button
              key={n.id}
              onClick={() => openItem(n)}
              className={`w-full text-left px-3 py-2 border-b last:border-b-0 hover:bg-hl ${n.read ? 'bg-white' : 'bg-amber-50'}`}
              data-testid={`notification-item-${n.id}`}
            >
              <div className="flex items-start gap-2">
                {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-red-600 shrink-0" />}
                <div className="min-w-0">
                  <p className={`text-sm ${n.read ? 'text-slate-700' : 'font-semibold text-slate-900'}`}>{n.title}</p>
                  <p className="text-xs text-slate-600 break-words">{n.message}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{timeAgo(n.created_at)}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
